import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ClipboardList, PlusCircle, Zap, Menu, Settings, LogOut, BarChart2, ScanLine, Package, Receipt, ShieldCheck, Database, Wrench, CircuitBoard } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { path: "/",          icon: LayoutDashboard, label: "Dashboard"    },
  { path: "/jobs",      icon: ClipboardList,   label: "All Jobs"     },
  { path: "/jobs/new",  icon: PlusCircle,      label: "New Job"      },
  { path: "/scan",      icon: ScanLine,        label: "Scan Barcode" },
  { path: "/billing",   icon: Receipt,         label: "Billing"      },
  { path: "/inventory", icon: Package,         label: "Inventory"    },
  { path: "/inventory/pcb", icon: CircuitBoard, label: "PCB Replacement", sub: true },
  { path: "/analytics", icon: BarChart2,    label: "Analytics"    },
  { path: "/admin",    icon: ShieldCheck, label: "Admin Panel"   },
  { path: "/migrate",  icon: Database,    label: "Data Migration"},
  { path: "/settings", icon: Settings,    label: "Settings"      },
];

const scNavItems = [
  { path: "/sc",     icon: Wrench,     label: "SC Jobs"     },
  { path: "/sc/new", icon: PlusCircle, label: "New SC Job"  },
];

export default function Layout() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    if (path === "/jobs") return location.pathname === "/jobs";
    if (path === "/sc")   return location.pathname === "/sc";
    if (path === "/inventory") return location.pathname === "/inventory";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-screen bg-[#f5faf6] overflow-hidden">
      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-60 bg-white border-r border-slate-200/80 flex flex-col shadow-sm transition-transform duration-300",
        "lg:static lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-200/80">
          <img
            src="/logo.jpg"
            alt="XSTRONG POWER"
            className="h-10 w-auto object-contain flex-shrink-0"
          />
          <div className="overflow-hidden">
            <p className="font-bold text-sm text-slate-900 leading-tight truncate" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>XSTRONG POWER</p>
            <p className="text-xs text-green-600 font-semibold leading-tight">EV CHARGER</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-2 mt-1">Menu</p>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
                item.sub && "ml-4 py-2 text-[13px]",
                isActive(item.path)
                  ? "bg-green-50 text-green-700 border border-green-100"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              )}
            >
              <item.icon size={item.sub ? 15 : 17} className={isActive(item.path) ? "text-green-600" : "text-slate-400"} />
              {item.label}
            </Link>
          ))}

          {/* Service Centre Section */}
          <div className="pt-3 mt-2 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-2">Service Centre</p>
            {scNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
                  isActive(item.path)
                    ? "bg-green-50 text-green-700 border border-green-100"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                )}
              >
                <item.icon size={17} className={isActive(item.path) ? "text-green-600" : "text-slate-400"} />
                {item.label}
              </Link>
            ))}
          </div>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200/80 space-y-2">
          <a href="/qr-poster" target="_blank" rel="noreferrer"
            className="flex items-center gap-2 text-xs text-green-700 hover:text-green-800 font-semibold transition-colors border border-green-300 rounded-lg px-2.5 py-1.5 hover:bg-green-50 bg-green-50">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
            Print QR Poster ↗
          </a>
          <a href="/request" target="_blank" rel="noreferrer"
            className="flex items-center gap-2 text-xs text-green-700 hover:text-green-800 font-medium transition-colors border border-green-200 rounded-lg px-2.5 py-1.5 hover:bg-green-50">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
            Customer Request Form ↗
          </a>
          <a href="/track" target="_blank" rel="noreferrer"
            className="flex items-center gap-2 text-xs text-slate-500 hover:text-green-700 transition-colors">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            Customer Tracking Portal
          </a>
          <button onClick={handleLogout}
            className="flex items-center gap-2 text-xs text-slate-400 hover:text-rose-600 transition-colors w-full"
            data-testid="logout-button">
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/25 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="h-14 bg-white border-b border-slate-200/80 flex items-center px-4 gap-3 lg:px-6 flex-shrink-0">
          <Button variant="ghost" size="icon" className="lg:hidden h-8 w-8" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="header-menu-button">
            <Menu className="h-4 w-4" />
          </Button>
          <div className="flex-1" />
          <Link to="/jobs/new" data-testid="sidebar-new-job-button">
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs font-medium">
              <PlusCircle className="w-3.5 h-3.5 mr-1.5" /> New Job
            </Button>
          </Link>
        </header>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
